"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.reportExportQuerySchema = exports.REPORT_EXPORT_FORMATS = exports.reportQuerySchema = exports.reportCategoryIdsParam = exports.CATEGORY_FILTERABLE_REPORT_IDS = exports.reportRangeQuerySchema = exports.OVERALL_REPORT_LINE_ROLES = exports.OVERALL_REPORT_LINES = exports.REPORT_TITLES = exports.REPORTS_BY_SECTION = exports.REPORT_IDS = exports.CARDS_REPORT_IDS = exports.CLIENTS_REPORT_IDS = exports.STORES_REPORT_IDS = exports.CAFE_REPORT_IDS = exports.RESERVATION_REPORT_IDS = exports.REPORT_SECTIONS = void 0;
exports.isReportId = isReportId;
exports.reportTitleKey = reportTitleKey;
exports.isCategoryFilterableReport = isCategoryFilterableReport;
const zod_1 = require("zod");
/**
 * Reports — SPEC §5.7.
 *
 * Five tabs of report cards (Reservations · Cafe · Stores · Clients · Cards)
 * plus the Overall Report, which gets its own page.
 *
 * EVERY REPORT FILTERS ON `businessDate`, NEVER `createdAt` (§4.1). A venue
 * that trades until 08:00 books a 02:00 sale against the previous day; getting
 * this wrong silently misstates every figure the owner is shown, and it fails
 * quietly because the totals still look plausible. The range is therefore
 * expressed as two BUSINESS DATES, not two instants — see
 * `resolveBusinessDate` / `businessDayBounds` in `./business-day`.
 *
 * ONE ENVELOPE FOR EVERY REPORT. There are 20+ named reports and all of them
 * must be printable and exportable to CSV and PDF (§5.7). Giving each its own
 * DTO would mean 20 renderers and 40 exporters; instead every report resolves
 * to {@link ReportTableDto} — typed columns plus rows — so one table component,
 * one CSV writer and one PDF writer serve the lot. The Overall Report is the
 * single exception: its two-panel layout is prescribed, so it has its own shape.
 */
/* -------------------------------------------------------------------------- */
/* Report catalogue                                                           */
/* -------------------------------------------------------------------------- */
/** SPEC §5.7 tab bar. */
exports.REPORT_SECTIONS = ['reservations', 'cafe', 'stores', 'clients', 'cards'];
/**
 * The eight Reservations reports, named exactly as SPEC §5.7 names them.
 * Renaming or dropping one is a spec violation, not a simplification.
 */
exports.RESERVATION_REPORT_IDS = [
    'reservations.byUser',
    'reservations.byDevice',
    'reservations.byCategory',
    'reservations.timeOut',
    'reservations.discounts',
    'reservations.times',
    'reservations.startStopActivity',
    'reservations.deviceGames',
];
/** The seven Cafe reports of SPEC §5.7. */
exports.CAFE_REPORT_IDS = [
    'cafe.byUser',
    'cafe.departmental',
    'cafe.categoriesDetailed',
    'cafe.products',
    'cafe.productsByDevice',
    'cafe.discounts',
    'cafe.times',
];
/**
 * Stores reports (§5.7: "stock movement, low stock, cost-vs-price margin per
 * product"). Named by us — the spec describes the job, not the label.
 */
exports.STORES_REPORT_IDS = [
    'stores.stockMovement',
    'stores.lowStock',
    'stores.margin',
    'stores.valuation',
];
/** Clients reports (§5.7: "spend per client, balances outstanding, top clients, new registrations"). */
exports.CLIENTS_REPORT_IDS = [
    'clients.spend',
    'clients.balances',
    'clients.top',
    'clients.newRegistrations',
    'clients.refills',
    /**
     * Electronic payments by provider and business day (2026-09-24) — what an
     * owner checks each provider's (FIB, Qi, FastPay) statement against.
     */
    'clients.electronicPayments',
];
/** Cards reports (§5.7: "card logins/usage"). */
exports.CARDS_REPORT_IDS = ['cards.logins', 'cards.usageByDevice', 'cards.unmatched'];
exports.REPORT_IDS = [
    ...exports.RESERVATION_REPORT_IDS,
    ...exports.CAFE_REPORT_IDS,
    ...exports.STORES_REPORT_IDS,
    ...exports.CLIENTS_REPORT_IDS,
    ...exports.CARDS_REPORT_IDS,
];
/** Which reports live under which tab, in render order. */
exports.REPORTS_BY_SECTION = {
    reservations: exports.RESERVATION_REPORT_IDS,
    cafe: exports.CAFE_REPORT_IDS,
    stores: exports.STORES_REPORT_IDS,
    clients: exports.CLIENTS_REPORT_IDS,
    cards: exports.CARDS_REPORT_IDS,
};
function isReportId(value) {
    return typeof value === 'string' && exports.REPORT_IDS.includes(value);
}
/**
 * i18n lookup key for a report's title.
 *
 * The dot in a report id is flattened to an underscore because i18next treats
 * `.` as a nesting separator: `reports.reportTitles.cafe.byUser` would be
 * resolved as four levels of object, not as one key named `cafe.byUser`, and
 * the lookup silently returns the raw key instead of the title. Derived here so
 * the server-rendered `titleKey` and the client's own lookups cannot disagree.
 */
function reportTitleKey(id) {
    return `reports.reportTitles.${id.replace('.', '_')}`;
}
/**
 * English fallback titles. The UI resolves {@link reportTitleKey} through i18n;
 * these are what the CSV and PDF exporters stamp on the file, since a
 * server-rendered export has no i18n context.
 */
exports.REPORT_TITLES = {
    'reservations.byUser': "Each user's reservation report",
    'reservations.byDevice': 'Device Reservations Reports',
    'reservations.byCategory': 'Category Reservations Report',
    'reservations.timeOut': 'Extra Time Report',
    'reservations.discounts': 'Reservation Discounts Report',
    'reservations.times': 'Reservation Times Reports',
    'reservations.startStopActivity': 'Start/Stop Activity Report',
    'reservations.deviceGames': 'Device Games Report',
    'cafe.byUser': 'Sales report for each user',
    'cafe.departmental': 'Department Sales Report',
    'cafe.categoriesDetailed': 'Detailed Categories Sales Report',
    'cafe.products': 'Product Sales Report',
    'cafe.productsByDevice': 'Detailed Device Product Sales',
    'cafe.discounts': 'Sales Discounts Report',
    'cafe.times': 'Sales Times Report',
    'stores.stockMovement': 'Items Stock Report',
    'stores.lowStock': 'Low Stock Report',
    'stores.margin': 'Cost vs Price Margin Report',
    'stores.valuation': 'Stock Valuation Report',
    'clients.spend': 'Spend per Client Report',
    'clients.balances': 'Outstanding Balances Report',
    'clients.top': 'Top Clients Report',
    'clients.newRegistrations': 'New Registrations Report',
    'clients.refills': 'Client Refills Report',
    'clients.electronicPayments': 'Electronic Payments Report',
    'cards.logins': 'Card Logins Report',
    'cards.usageByDevice': 'Card Usage by Device Report',
    'cards.unmatched': 'Unrecognised Card Taps Report',
};
/**
 * The right-panel lines, in the order SPEC §5.7 prescribes. Rendering must
 * follow this array rather than object key order.
 *
 * `refillTopUps` and `refillReturns` sit straight under `refill` (owner,
 * 2026-10-14): Refill is net of the refunds, so the two figures behind it - what
 * was topped up and what was refunded - read under it as plain arithmetic
 * (Refill = Topped up - Refunded) rather than among the deductions.
 */
exports.OVERALL_REPORT_LINES = [
    'reservationSales',
    'cafeSales',
    'refill',
    'refillTopUps',
    'refillReturns',
    'rechargeBonus',
    'reservationDiscounts',
    'cafeDiscounts',
    'incomes',
    'expenses',
    'netProfit',
];
/**
 * How each line behaves in the arithmetic, so the UI can style it without
 * re-deriving the rules:
 *  - `revenue`    counts INTO Incomes
 *  - `deduction`  subtracts from Net Profit
 *  - `memo`       shown for information only, not in either subtotal
 *  - `detail`     a figure behind the Refill line above it (what was topped up,
 *                 what was refunded), already INSIDE that line: neither added
 *                 to Incomes nor deducted from Net Profit
 *  - `subtotal` / `result` are the computed lines
 */
exports.OVERALL_REPORT_LINE_ROLES = {
    reservationSales: 'revenue',
    cafeSales: 'revenue',
    refill: 'revenue',
    // A recharge bonus is money the venue GAVE AWAY as balance, not money taken.
    // The incumbent's own figures show it excluded from both Incomes and Net
    // Profit (SPEC §14), so it is displayed as a memo line.
    rechargeBonus: 'memo',
    reservationDiscounts: 'deduction',
    cafeDiscounts: 'deduction',
    // Balance refunds are netted into the Refill line itself (owner, 2026-10-14),
    // so these two are the arithmetic INSIDE that figure: Topped up - Refunded =
    // Refill. Styled as a deduction the refund would read as taken off a second
    // time, which is exactly what Net Profit no longer does; Topped up is not
    // revenue of its own either, or Incomes would count the top-ups twice.
    refillTopUps: 'detail',
    refillReturns: 'detail',
    incomes: 'subtotal',
    expenses: 'deduction',
    netProfit: 'result',
};
/* -------------------------------------------------------------------------- */
/* Queries                                                                    */
/* -------------------------------------------------------------------------- */
const businessDateString = zod_1.z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a YYYY-MM-DD business date.');
/**
 * An instant from a date-time picker. The server resolves it to the business
 * date containing it, so the Overall Report's "25/7 09:00 -> 26/7 08:00"
 * default and a plain "25/7" both land on business date 2026-07-25.
 */
const isoInstant = zod_1.z
    .string()
    .trim()
    .refine((value) => !Number.isNaN(Date.parse(value)), 'Expected an ISO date-time.');
/**
 * From/To for every report. Either form is accepted:
 *  - `fromBusinessDate` / `toBusinessDate` — explicit business dates;
 *  - `from` / `to` — instants from the date-time pickers, resolved server-side.
 * Omitting everything means the current business day.
 */
exports.reportRangeQuerySchema = zod_1.z
    .object({
    fromBusinessDate: businessDateString.optional(),
    toBusinessDate: businessDateString.optional(),
    from: isoInstant.optional(),
    to: isoInstant.optional(),
})
    .refine((value) => !(value.fromBusinessDate && value.toBusinessDate) ||
    value.fromBusinessDate <= value.toBusinessDate, { message: 'The From date is after the To date.', path: ['toBusinessDate'] });
/**
 * Reports that honour a category filter (owner, 2026-09-23: "in the detailed
 * category sales i should be able to select the categories i want"). Every
 * other report ignores `categoryIds`, so the picker belongs only on these.
 */
exports.CATEGORY_FILTERABLE_REPORT_IDS = [
    'cafe.categoriesDetailed',
    'cafe.products',
];
function isCategoryFilterableReport(id) {
    return exports.CATEGORY_FILTERABLE_REPORT_IDS.includes(id);
}
/**
 * Product-category ids to restrict a report to. Omitted or empty = every
 * category.
 *
 * A query string carries a list two ways — `?categoryIds=a,b` and
 * `?categoryIds=a&categoryIds=b` (which arrives as an array) — and the page
 * must not have to know which one the server expects, so both are accepted
 * and may even be mixed. Blanks are dropped and repeats collapse: it is a set.
 * An empty result normalises to `undefined`, so "no filter" is one state.
 */
exports.reportCategoryIdsParam = zod_1.z.preprocess((value) => {
    if (value === undefined || value === null)
        return undefined;
    const parts = (Array.isArray(value) ? value : [value]).flatMap((part) => typeof part === 'string' ? part.split(',') : [part]);
    const ids = [
        ...new Set(parts.map((part) => (typeof part === 'string' ? part.trim() : part))),
    ].filter((part) => part !== '');
    return ids.length > 0 ? ids : undefined;
}, zod_1.z.array(zod_1.z.string().min(1).max(64)).max(200).optional());
/** A named report plus its range. */
exports.reportQuerySchema = exports.reportRangeQuerySchema.safeExtend({
    /** Cap on rows returned; exports raise it. */
    limit: zod_1.z.coerce.number().int().min(1).max(5000).default(500),
    /** See {@link reportCategoryIdsParam}; honoured by {@link CATEGORY_FILTERABLE_REPORT_IDS}. */
    categoryIds: exports.reportCategoryIdsParam,
});
exports.REPORT_EXPORT_FORMATS = ['csv', 'pdf'];
exports.reportExportQuerySchema = exports.reportRangeQuerySchema.safeExtend({
    format: zod_1.z.enum(exports.REPORT_EXPORT_FORMATS),
    /** The same filter as the table, so an export matches what is on screen. */
    categoryIds: exports.reportCategoryIdsParam,
});
//# sourceMappingURL=reports.js.map